import React, { useState, useEffect, useCallback } from 'react';
import {
  ComplianceMonitoringProgramme,
  ComplianceMonitoringPlanItem,
  ComplianceReview,
  MonitoringDashboardKPIs,
} from './types';
import { monitoringService } from './monitoringService';
import { ComplianceMonitoringMetricsCards } from './ComplianceMonitoringMetricsCards';
import { ComplianceReviewTable } from './ComplianceReviewTable';
import { ComplianceReviewDetailModal } from './ComplianceReviewDetailModal';
import { ComplianceProgrammeModal } from './ComplianceProgrammeModal';

interface ComplianceMonitoringPageProps {
  organizationId?: string;
}

export const ComplianceMonitoringPage: React.FC<ComplianceMonitoringPageProps> = ({
  organizationId = 'ORG-XYZ',
}) => {
  const [metrics, setMetrics] = useState<MonitoringDashboardKPIs | null>(null);
  const [reviews, setReviews] = useState<ComplianceReview[]>([]);
  const [programmes, setProgrammes] = useState<ComplianceMonitoringProgramme[]>([]);
  const [planItems, setPlanItems] = useState<ComplianceMonitoringPlanItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Modals state
  const [selectedReviewId, setSelectedReviewId] = useState<string | null>(null);
  const [showProgrammeModal, setShowProgrammeModal] = useState(false);
  const [showCreateReviewModal, setShowCreateReviewModal] = useState(false);

  // New review form state
  const [newReview, setNewReview] = useState({
    title: '',
    review_type: 'ROUTINE',
    review_method: 'ONSITE_AUDIT',
    target_department: 'Flight Operations',
    lead_reviewer_name: 'Lead Compliance Auditor',
    independence_verified: true,
    independence_notes: 'External to target operational unit',
    scheduled_start_date: new Date().toISOString().split('T')[0],
    scheduled_end_date: new Date(Date.now() + 7 * 86400000).toISOString().split('T')[0],
    scope_description: 'EASA / SHGM Compliance routine oversight check',
  });

  const loadData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [metricsData, reviewsData, programmesData, planItemsData] = await Promise.all([
        monitoringService.getMetrics(organizationId),
        monitoringService.getReviews(organizationId),
        monitoringService.getProgrammes(organizationId),
        monitoringService.getPlanItems(organizationId),
      ]);
      setMetrics(metricsData);
      setReviews(reviewsData);
      setProgrammes(programmesData);
      setPlanItems(planItemsData);
    } catch (err) {
      console.error('Failed to load compliance monitoring data:', err);
      setError('Failed to load compliance data: ' + (err as Error).message);
    } finally {
      setIsLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleCreateReview = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const reviewNumber = `REV-${new Date().getFullYear()}-${String(reviews.length + 1).padStart(3, '0')}`;
      await monitoringService.createReview({
        ...newReview,
        organization_id: organizationId,
        review_number: reviewNumber,
        scheduled_start: newReview.scheduled_start_date,
        scheduled_end: newReview.scheduled_end_date,
        lead_reviewer_id: 'USR-CMP-001',
        status: 'PLANNED',
      });
      setShowCreateReviewModal(false);
      setNewReview({
        title: '',
        review_type: 'ROUTINE',
        review_method: 'ONSITE_AUDIT',
        target_department: 'Flight Operations',
        lead_reviewer_name: 'Lead Compliance Auditor',
        independence_verified: true,
        independence_notes: 'External to target operational unit',
        scheduled_start_date: new Date().toISOString().split('T')[0],
        scheduled_end_date: new Date(Date.now() + 7 * 86400000).toISOString().split('T')[0],
        scope_description: 'EASA / SHGM Compliance routine oversight check',
      });
      loadData();
    } catch (err) {
      alert('Failed to schedule review: ' + (err as Error).message);
    }
  };

  const selectedReview = reviews.find((r) => r.id === selectedReviewId);

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Page Header */}
      <div className="page-header-banner">
        <div className="badge">REGULATORY & COMPLIANCE</div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px' }}>
          <div>
            <h1>Compliance Monitoring & Oversight</h1>
            <p>Annual audit programmes, scheduled management reviews, independent assurance audits, and CAPA bridging.</p>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={() => setShowProgrammeModal(true)}
              className="btn-secondary"
              style={{ width: 'auto' }}
            >
              📅 Program & Plans
            </button>
            <button
              onClick={() => setShowCreateReviewModal(true)}
              className="btn-primary"
              style={{ width: 'auto', padding: '10px 18px' }}
            >
              + New Review
            </button>
          </div>
        </div>
      </div>

      {/* Error state */}
      {error && (
        <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
          {error}
        </div>
      )}

      {/* KPI Cards */}
      <ComplianceMonitoringMetricsCards kpis={metrics} metrics={metrics} />

      {/* Reviews Table */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-white">Compliance Reviews & Oversight Records</h2>
          <span className="text-xs text-slate-400">Showing {reviews.length} total records</span>
        </div>

        <ComplianceReviewTable reviews={reviews} onSelectReview={(rev: any) => setSelectedReviewId(rev?.id || rev)} />
      </div>

      {/* Review Detail Modal */}
      {selectedReview && (
        <ComplianceReviewDetailModal
          review={selectedReview}
          onClose={() => setSelectedReviewId(null)}
          onRefresh={loadData}
        />
      )}

      {/* Programme Modal */}
      {showProgrammeModal && (
        <ComplianceProgrammeModal
          programmes={programmes}
          planItems={planItems}
          organizationId={organizationId}
          onClose={() => setShowProgrammeModal(false)}
          onRefresh={loadData}
        />
      )}

      {/* Create Review Modal */}
      {showCreateReviewModal && (
        <div className="modal-backdrop">
          <div className="modal-card max-w-xl">
            <div className="modal-header">
              <div>
                <h3>Schedule Compliance Review</h3>
                <p>Plan internal audits, oversight assessments, and independent evaluations</p>
              </div>
              <button
                type="button"
                onClick={() => setShowCreateReviewModal(false)}
                className="close-btn"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleCreateReview}>
              <div className="modal-body space-y-4">
                <div className="form-field">
                  <label>Title & Review Definition *</label>
                  <input
                    type="text"
                    value={newReview.title}
                    onChange={(e) => setNewReview({ ...newReview, title: e.target.value })}
                    required
                    placeholder="e.g. EASA Part-CAT Fuel Policy Operational Audit"
                  />
                </div>

                <div className="form-grid-2">
                  <div className="form-field">
                    <label>Review Type *</label>
                    <select
                      value={newReview.review_type}
                      onChange={(e) => setNewReview({ ...newReview, review_type: e.target.value })}
                    >
                      <option value="ROUTINE">ROUTINE (Routine Oversight)</option>
                      <option value="SPECIAL">SPECIAL (Special Ad-hoc)</option>
                      <option value="FOCUSED">FOCUSED (Focused Deep Dive)</option>
                      <option value="FOLLOW_UP">FOLLOW_UP (Corrective Action Follow-up)</option>
                    </select>
                  </div>
                  <div className="form-field">
                    <label>Review Method *</label>
                    <select
                      value={newReview.review_method}
                      onChange={(e) => setNewReview({ ...newReview, review_method: e.target.value })}
                    >
                      <option value="ONSITE_AUDIT">Onsite Audit</option>
                      <option value="DESK_ASSESSMENT">Desk Assessment</option>
                      <option value="INSPECTION">Physical Inspection</option>
                      <option value="SAMPLING">Statistical Sampling</option>
                    </select>
                  </div>
                </div>

                <div className="form-grid-2">
                  <div className="form-field">
                    <label>Target Department *</label>
                    <input
                      type="text"
                      value={newReview.target_department}
                      onChange={(e) => setNewReview({ ...newReview, target_department: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-field">
                    <label>Lead Reviewer / Auditor *</label>
                    <input
                      type="text"
                      value={newReview.lead_reviewer_name}
                      onChange={(e) => setNewReview({ ...newReview, lead_reviewer_name: e.target.value })}
                      required
                    />
                  </div>
                </div>

                <div className="form-grid-2">
                  <div className="form-field">
                    <label>Scheduled Start Date *</label>
                    <input
                      type="date"
                      value={newReview.scheduled_start_date}
                      onChange={(e) => setNewReview({ ...newReview, scheduled_start_date: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-field">
                    <label>Scheduled End Date *</label>
                    <input
                      type="date"
                      value={newReview.scheduled_end_date}
                      onChange={(e) => setNewReview({ ...newReview, scheduled_end_date: e.target.value })}
                      required
                    />
                  </div>
                </div>

                <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg space-y-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={newReview.independence_verified}
                      onChange={(e) => setNewReview({ ...newReview, independence_verified: e.target.checked })}
                    />
                    <span className="text-xs text-slate-800 font-semibold">
                      Independence Verification Confirmed
                    </span>
                  </label>
                  <input
                    type="text"
                    placeholder="Independence justification / departmental segregation notes..."
                    value={newReview.independence_notes}
                    onChange={(e) => setNewReview({ ...newReview, independence_notes: e.target.value })}
                    className="w-full text-xs"
                  />
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  onClick={() => setShowCreateReviewModal(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                >
                  Schedule Review
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
